// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: f88d4459-7fd8-41b9-8300-751ae244811d

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Divider, Alert, AlertTitle, Button, Slider,
  FormControlLabel, Switch, Tooltip, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LoopIcon from '@mui/icons-material/Loop';
import HubIcon from '@mui/icons-material/Hub';
import PushPinIcon from '@mui/icons-material/PushPin';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { PICKLISTS } from './schema';
import { exportCsv } from './exports';
import store from './store';
import HelpBubble from './HelpBubble';

// ---------------------------------------------------------------------------
// Primordia — Wave Planner.
//
// RoadmapView SHOWS the waves somebody already chose. This screen CHOOSES them.
//
// Ordering applications for a migration is not a judgement call and it is not a
// generative-AI task: it is a graph problem with a provably correct answer. An
// application cannot cut over before the interface engine it feeds, and two
// applications that talk to each other both ways cannot be ordered at all —
// they have to move together. So every number on this screen is COMPUTED, and
// every row states the arithmetic that produced it. Nothing here is guessed.
//
// The pipeline, in order:
//
//   1. Build a directed graph. Node = application, plus a synthetic node for
//      every interface engine named in `integrations[].via` that is not itself
//      an application in the inventory (Corepoint, Rhapsody, Cloverleaf ...).
//      Edge dependency -> dependent, meaning "the dependency migrates first".
//   2. Condense strongly-connected components (Tarjan). A component with more
//      than one member is a genuine dependency CYCLE — Airstrip <-> GE Centricity
//      is the classic one. A topological sort cannot order a cycle, so we do not
//      pretend: the whole component becomes ONE unit that moves together, and it
//      is reported to the user as such. The condensation is always a DAG, which
//      is what makes the rest of the pipeline terminate.
//   3. Optionally merge units that share an interface engine (cohesion), with a
//      cycle guard: a merge that would create a cycle is rejected and reported
//      rather than silently applied.
//   4. Kahn's algorithm over the merged DAG gives a topological order; longest-
//      path layering over that order gives the wave number. Pins, the risk-
//      deferral rule and the per-wave capacity cap are applied as floors during
//      the same pass, so the dependency invariant is never violated by them.
//
// Result: a dependency is never scheduled after its dependent, and where that
// is impossible (a cycle, or a manual pin that contradicts the graph) the
// planner says so out loud instead of quietly breaking the rule.
// ---------------------------------------------------------------------------

// Optimum palette.
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const GREEN = '#2E7D32';
const AMBER = '#F5A623';
const RED = '#C62828';

const NOT_SCHEDULED = 'Not Scheduled';

// The tenant-agnostic wave labels, in plan order ("Not Scheduled" is the
// backlog, never a wave).
const STANDARD_WAVES = PICKLISTS.migrationWave.filter((w) => w !== NOT_SCHEDULED);

const norm = (s) => String(s === undefined || s === null ? '' : s).trim().toLowerCase();

// Wave number (1-based) -> label. Beyond the picklist we keep counting rather
// than clamping: a plan that genuinely needs five layers must say five.
const waveLabel = (n) => STANDARD_WAVES[n - 1] || `Wave ${n}`;

// Label -> wave number, for reading an existing manual assignment back in.
const waveNumber = (label) => {
  const s = String(label || '').trim();
  if (!s || s === NOT_SCHEDULED) return null;
  const idx = STANDARD_WAVES.indexOf(s);
  if (idx >= 0) return idx + 1;
  const m = /(\d+)/.exec(s);
  return m ? Number(m[1]) : null;
};

const tagVal = (a, group) => (a.tags || []).find((t) => t.group === group)?.value;
const currentWaveOf = (a) => (a.fields?.cmWave || '').trim() || NOT_SCHEDULED;

// "Schedule these later" population: Mission Critical business criticality, or
// DR Tier 1 (captured either as a grouped tag or as the legacy field).
const isHighRisk = (a) => {
  if (/mission critical/i.test(String(a.fields?.businessCriticality || ''))) return true;
  const tier = String(tagVal(a, 'DR Tier') || a.fields?.drTier || '');
  return /\b1\b/.test(tier);
};

// An integration row only counts if it actually names something.
const usableIntegrations = (a) => (a.integrations || [])
  .filter((it) => it && (norm(it.to) || norm(it.via)));

// ===========================================================================
// 1. GRAPH CONSTRUCTION
// ---------------------------------------------------------------------------
// Nodes: every application, plus one synthetic node per interface engine that
// is named in a `via` but has no fact sheet of its own. An engine is shared
// infrastructure — many applications depend on it, so it must land no later
// than the earliest application that rides it.
//
// Edges point dependency -> dependent. Matching is case-insensitive on name,
// which is how the rest of the app (RoadmapView, ReadinessView, InterfaceHub)
// already resolves `to`/`via` values.
// ===========================================================================
function buildGraph(apps) {
  const appByName = new Map();
  apps.forEach((a) => {
    const k = norm(a.name);
    if (k && !appByName.has(k)) appByName.set(k, a);
  });

  const nodes = [];
  const indexByKey = new Map();
  const addNode = (key, node) => {
    if (indexByKey.has(key)) return indexByKey.get(key);
    const i = nodes.length;
    nodes.push({ key, ...node });
    indexByKey.set(key, i);
    return i;
  };

  // Application nodes first, so node index order is stable and alphabetical
  // (listFactSheets already sorts by name) — that keeps the plan deterministic.
  apps.forEach((a) => addNode(`app:${a.id}`, { kind: 'app', name: a.name || '(unnamed)', app: a }));

  // Resolve a free-text endpoint to an application node, or -1 when it is an
  // external endpoint we hold no fact sheet for (vendor SaaS, IdP, ...).
  const resolveApp = (raw) => {
    const hit = appByName.get(norm(raw));
    return hit ? indexByKey.get(`app:${hit.id}`) : -1;
  };

  const edges = [];
  const edgeKeys = new Set();
  const addEdge = (from, to, kind, viaName) => {
    if (from < 0 || to < 0 || from === to) return;
    const sig = `${from}>${to}`;
    if (edgeKeys.has(sig)) return;
    edgeKeys.add(sig);
    edges.push({ from, to, kind, viaName: viaName || '' });
  };

  // engine node index -> Set of application node indexes riding that engine
  const engineMembers = new Map();
  let externalEndpoints = 0;

  apps.forEach((a) => {
    const self = indexByKey.get(`app:${a.id}`);
    usableIntegrations(a).forEach((it) => {
      // (a) the far end of the integration
      if (norm(it.to)) {
        const target = resolveApp(it.to);
        if (target < 0) externalEndpoints += 1;
        else addEdge(target, self, 'integration', '');
      }
      // (b) the interface engine in the middle, if any
      const via = String(it.via || '').trim();
      if (via) {
        let engine = resolveApp(via);
        if (engine < 0) engine = addNode(`engine:${norm(via)}`, { kind: 'engine', name: via, app: null });
        addEdge(engine, self, 'engine', via);
        if (!engineMembers.has(engine)) engineMembers.set(engine, new Set());
        engineMembers.get(engine).add(self);
      }
    });
  });

  const outAdj = nodes.map(() => []);
  const inAdj = nodes.map(() => []);
  edges.forEach((e) => { outAdj[e.from].push(e.to); inAdj[e.to].push(e); });

  return { nodes, edges, outAdj, inAdj, engineMembers, externalEndpoints };
}

// ===========================================================================
// 2. STRONGLY-CONNECTED COMPONENTS — Tarjan, iterative.
// ---------------------------------------------------------------------------
// THE cycle-handling step. Two applications that integrate bidirectionally
// (Airstrip <-> GE Centricity) sit in the same SCC. There is no correct order
// for them, so we refuse to invent one: the SCC is collapsed into a single node
// and every member is scheduled in the same wave. That is both the honest
// answer and the operationally correct one — they cut over together.
//
// Iterative (explicit stack) rather than recursive so a deep dependency chain
// in a large inventory cannot blow the JS call stack.
//
// Returns comp[node] = component id, and comps[id] = [node, ...]. Component ids
// come out in reverse topological order, which we do not rely on — Kahn below
// re-derives the order explicitly.
// ===========================================================================
function stronglyConnectedComponents(nodeCount, outAdj) {
  const index = new Array(nodeCount).fill(-1);
  const low = new Array(nodeCount).fill(0);
  const onStack = new Array(nodeCount).fill(false);
  const comp = new Array(nodeCount).fill(-1);
  const comps = [];
  const stack = [];
  let counter = 0;

  for (let root = 0; root < nodeCount; root += 1) {
    if (index[root] !== -1) continue;
    // frame = [node, nextChildPointer]; pointer > 0 means "already initialised".
    const work = [[root, 0]];
    while (work.length) {
      const frame = work[work.length - 1];
      const v = frame[0];
      if (frame[1] === 0 && index[v] === -1) {
        index[v] = counter;
        low[v] = counter;
        counter += 1;
        stack.push(v);
        onStack[v] = true;
      }
      let descended = false;
      const kids = outAdj[v];
      while (frame[1] < kids.length) {
        const w = kids[frame[1]];
        frame[1] += 1;
        if (index[w] === -1) { work.push([w, 0]); descended = true; break; }
        if (onStack[w]) low[v] = Math.min(low[v], index[w]);
      }
      if (descended) continue;

      // v is finished: if it is a root of an SCC, pop the whole component.
      if (low[v] === index[v]) {
        const members = [];
        for (;;) {
          const w = stack.pop();
          onStack[w] = false;
          members.push(w);
          if (w === v) break;
        }
        members.sort((x, y) => x - y);
        members.forEach((m) => { comp[m] = comps.length; });
        comps.push(members);
      }
      work.pop();
      if (work.length) {
        const parent = work[work.length - 1][0];
        low[parent] = Math.min(low[parent], low[v]);
      }
    }
  }
  return { comp, comps };
}

// --- Union-find, used only to merge units that share an interface engine ----
function makeUnionFind(n) {
  const parent = new Array(n);
  for (let i = 0; i < n; i += 1) parent[i] = i;
  const find = (x) => {
    let r = x;
    while (parent[r] !== r) r = parent[r];
    let c = x;
    while (parent[c] !== c) { const next = parent[c]; parent[c] = r; c = next; }
    return r;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    // Always attach the larger id under the smaller so the representative is
    // deterministic regardless of merge order.
    if (ra < rb) parent[rb] = ra; else parent[ra] = rb;
  };
  return { find, union };
}

// Is a proposed set of merges legal? Two ways it can fail, and both are real:
//
//   (a) the merge swallows an ordering edge — one member of the group depends
//       on another, so they cannot share a wave (X -> Y merged into {X,Y});
//   (b) the merge creates a cycle between merged nodes — A->B and C->D with
//       {A,D} and {B,C} merged leaves no valid order at all.
//
// Either way there is no wave assignment that satisfies the merge AND the
// dependency graph, so the group is rejected and reported rather than applied.
function mergedGraphIsAcyclic(compCount, compEdges, uf) {
  const roots = new Set();
  for (let c = 0; c < compCount; c += 1) roots.add(uf.find(c));
  const indeg = new Map();
  const adj = new Map();
  roots.forEach((r) => { indeg.set(r, 0); adj.set(r, []); });
  let swallowed = false;
  compEdges.forEach((e) => {
    const a = uf.find(e.from);
    const b = uf.find(e.to);
    if (a === b) { swallowed = true; return; } // (a)
    adj.get(a).push(b);
    indeg.set(b, indeg.get(b) + 1);
  });
  if (swallowed) return false;
  const queue = [];
  roots.forEach((r) => { if (indeg.get(r) === 0) queue.push(r); });
  let seen = 0;
  while (queue.length) {
    const u = queue.shift();
    seen += 1;
    adj.get(u).forEach((v) => {
      indeg.set(v, indeg.get(v) - 1);
      if (indeg.get(v) === 0) queue.push(v);
    });
  }
  return seen === roots.size;
}

// ===========================================================================
// 3+4. THE PLANNER
// ---------------------------------------------------------------------------
// Pure function of (apps, options). Same inputs -> same plan, every time.
// ===========================================================================
function buildPlan(apps, options) {
  const { maxPerWave, groupEngines, deferHighRisk, respectPins } = options;

  const graph = buildGraph(apps);
  const { nodes, edges, inAdj, outAdj, engineMembers, externalEndpoints } = graph;
  const nodeCount = nodes.length;

  const { comp, comps } = stronglyConnectedComponents(nodeCount, outAdj);
  const compCount = comps.length;

  // Component-level edges (the condensation — guaranteed acyclic).
  const compEdges = [];
  const compEdgeKeys = new Set();
  edges.forEach((e) => {
    const a = comp[e.from];
    const b = comp[e.to];
    if (a === b) return; // internal to a cycle: already handled by moving together
    const sig = `${a}>${b}`;
    if (compEdgeKeys.has(sig)) return;
    compEdgeKeys.add(sig);
    compEdges.push({ from: a, to: b });
  });

  // --- Engine cohesion -----------------------------------------------------
  // "Apps sharing an interface engine cut over as a unit." Modelled as an
  // equality constraint: merge their components. Each group is trialled in a
  // deterministic order and accepted only if the merged graph stays acyclic;
  // a rejected group is reported, not silently dropped.
  const engineGroups = [...engineMembers.entries()]
    .map(([engineNode, memberSet]) => ({
      engineNode,
      name: nodes[engineNode].name,
      appNodes: [...memberSet].sort((x, y) => x - y),
    }))
    .filter((g) => g.appNodes.length > 1)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Only the RIDERS are merged, never the engine itself: the engine is the
  // dependency of everything on it, so it has to land in an earlier wave. The
  // engine cuts over first, then its applications move together.
  const unionGroup = (uf, g) => {
    const ids = g.appNodes.map((n) => comp[n]);
    ids.forEach((c) => uf.union(ids[0], c));
  };

  const acceptedGroups = [];
  const rejectedGroups = [];
  if (groupEngines) {
    engineGroups.forEach((g) => {
      const trial = makeUnionFind(compCount);
      [...acceptedGroups, g].forEach((grp) => unionGroup(trial, grp));
      if (mergedGraphIsAcyclic(compCount, compEdges, trial)) acceptedGroups.push(g);
      else rejectedGroups.push(g);
    });
  }

  const uf = makeUnionFind(compCount);
  acceptedGroups.forEach((g) => unionGroup(uf, g));

  // --- Units: the things that actually get a wave --------------------------
  const unitIdByRoot = new Map();
  const unitOfComp = new Array(compCount).fill(-1);
  const units = [];
  for (let c = 0; c < compCount; c += 1) {
    const root = uf.find(c);
    if (!unitIdByRoot.has(root)) {
      unitIdByRoot.set(root, units.length);
      units.push({ id: units.length, comps: [], nodes: [], appNodes: [], engineNodes: [] });
    }
    const u = unitIdByRoot.get(root);
    unitOfComp[c] = u;
    units[u].comps.push(c);
  }
  for (let c = 0; c < compCount; c += 1) {
    const u = units[unitOfComp[c]];
    comps[c].forEach((n) => {
      u.nodes.push(n);
      if (nodes[n].kind === 'app') u.appNodes.push(n); else u.engineNodes.push(n);
    });
  }
  units.forEach((u) => {
    u.nodes.sort((x, y) => x - y);
    u.appNodes.sort((x, y) => x - y);
    u.size = u.appNodes.length;
    u.highRisk = u.appNodes.some((n) => isHighRisk(nodes[n].app));
    // A pin is honoured; when a unit carries conflicting pins the LATEST wins,
    // because moving the group earlier than a pinned member would break it.
    const pins = respectPins
      ? u.appNodes.map((n) => waveNumber(nodes[n].app.fields?.cmWave)).filter((x) => x !== null)
      : [];
    u.pin = pins.length ? Math.max(...pins) : null;
    u.sortKey = u.nodes.map((n) => norm(nodes[n].name)).sort()[0] || '';
  });
  const unitOfNode = (n) => unitOfComp[comp[n]];

  // --- Unit-level DAG ------------------------------------------------------
  const unitCount = units.length;
  const preds = units.map(() => new Set());
  const succs = units.map(() => new Set());
  compEdges.forEach((e) => {
    const a = unitOfComp[e.from];
    const b = unitOfComp[e.to];
    if (a === b) return;
    preds[b].add(a);
    succs[a].add(b);
  });

  // --- Kahn's algorithm: topological order of the units --------------------
  // Ties are broken by unit sort key (lowest member name) so the output does
  // not depend on Map/Set iteration order.
  const indeg = units.map((u) => preds[u.id].size);
  const ready = [];
  for (let u = 0; u < unitCount; u += 1) if (indeg[u] === 0) ready.push(u);
  const byKey = (a, b) => units[a].sortKey.localeCompare(units[b].sortKey) || (a - b);
  ready.sort(byKey);
  const order = [];
  while (ready.length) {
    const u = ready.shift();
    order.push(u);
    const unlocked = [];
    succs[u].forEach((v) => {
      indeg[v] -= 1;
      if (indeg[v] === 0) unlocked.push(v);
    });
    if (unlocked.length) {
      unlocked.sort(byKey);
      unlocked.forEach((v) => ready.push(v));
      ready.sort(byKey);
    }
  }
  // The condensation is acyclic by construction and cohesion merges were cycle-
  // guarded, so every unit must appear. This is belt-and-braces only.
  if (order.length !== unitCount) {
    for (let u = 0; u < unitCount; u += 1) if (!order.includes(u)) order.push(u);
  }

  // --- Longest-path layering ----------------------------------------------
  // wave(u) = 1 + max(wave(dependencies)), i.e. every dependency is strictly
  // earlier. `floors` carries the extra minimums imposed by the capacity cap.
  const assign = (floors) => {
    const wave = new Array(unitCount).fill(1);
    const base = new Array(unitCount).fill(1);
    order.forEach((u) => {
      let b = 1;
      preds[u].forEach((p) => { b = Math.max(b, wave[p] + 1); });
      if (deferHighRisk && units[u].highRisk) b += 1;
      b = Math.max(b, floors[u]);
      base[u] = b;
      wave[u] = units[u].pin !== null ? units[u].pin : b;
    });
    return { wave, base };
  };

  const noFloors = new Array(unitCount).fill(1);
  const natural = assign(noFloors);

  // --- Capacity: max apps per wave ----------------------------------------
  // Overflow is pushed one wave later and the whole layering is recomputed, so
  // dependents follow their dependencies down. Pinned units never spill (the
  // pin is a promise). A single unit larger than the cap is left in place and
  // reported — an indivisible cycle cannot be split to satisfy a slider.
  const floors = new Array(unitCount).fill(1);
  const spilled = new Set();
  const overSizedUnits = [];
  let result = natural;
  const cap = Math.max(1, maxPerWave);
  const limit = unitCount + 8;
  for (let round = 0; round < limit; round += 1) {
    const pass = assign(floors);
    result = pass;
    const byWave = new Map();
    order.forEach((u) => {
      const w = pass.wave[u];
      if (!byWave.has(w)) byWave.set(w, []);
      byWave.get(w).push(u);
    });
    let changed = false;
    [...byWave.keys()].sort((a, b) => a - b).some((w) => {
      const list = byWave.get(w).slice().sort(byKey);
      let used = 0;
      let placedOne = false;
      list.forEach((u) => {
        const pinnedHere = units[u].pin !== null;
        const fits = used + units[u].size <= cap;
        if (!placedOne || fits || pinnedHere) {
          used += units[u].size;
          placedOne = true;
          return;
        }
        floors[u] = Math.max(floors[u], w + 1);
        spilled.add(u);
        changed = true;
      });
      return changed; // recompute the whole layering before touching later waves
    });
    if (!changed) break;
  }
  units.forEach((u) => { if (u.size > cap) overSizedUnits.push(u); });

  const waveOfUnit = result.wave;
  const waveOfNode = nodes.map((n, i) => waveOfUnit[unitOfNode(i)]);

  // --- Per-application rows, each carrying its own arithmetic --------------
  const engineOfApp = new Map(); // app node -> [{ name, count }]
  acceptedGroups.forEach((g) => {
    g.appNodes.forEach((n) => {
      if (!engineOfApp.has(n)) engineOfApp.set(n, []);
      engineOfApp.get(n).push({ name: g.name, count: g.appNodes.length });
    });
  });

  const pinConflicts = [];
  const rows = [];
  nodes.forEach((node, i) => {
    if (node.kind !== 'app') return;
    const a = node.app;
    const u = unitOfNode(i);
    const unit = units[u];
    const wave = waveOfUnit[u];
    const current = currentWaveOf(a);
    const proposed = waveLabel(wave);
    const reasons = [];

    // (a) manual pin
    if (unit.pin !== null) {
      const pinnedHere = respectPins && waveNumber(a.fields?.cmWave) !== null;
      if (pinnedHere) reasons.push(`Pinned manually to ${current}`);
      else reasons.push(`Held with a manually pinned app in ${waveLabel(unit.pin)}`);
      if (result.base[u] > unit.pin) {
        const detail = `pinned to ${waveLabel(unit.pin)} but its dependencies land in ${waveLabel(result.base[u] - 1)}`;
        reasons.push(`Pin conflict — ${detail}`);
        pinConflicts.push({ id: a.id, name: a.name, detail });
      }
    }

    // (b) cycle membership — the honest answer, not a broken tie
    const cycle = comps[comp[i]];
    if (cycle.length > 1) {
      const names = cycle.map((n) => nodes[n].name);
      reasons.push(`Mutual dependency group of ${cycle.length} (must move together): ${names.join(', ')}`);
    }

    // (c) the binding dependency: the one that actually set the floor. Members
    // of the same cycle are excluded — they are reported as the cycle, not as
    // an ordering reason, because within a cycle there is no "first".
    const deps = inAdj[i]
      .filter((e) => comp[e.from] !== comp[i])
      .map((e) => ({ name: nodes[e.from].name, wave: waveOfNode[e.from] }))
      .sort((x, y) => x.name.localeCompare(y.name));
    const binding = deps.filter((d) => d.wave === wave - 1);
    if (binding.length) {
      const shown = binding.slice(0, 2).map((d) => `${d.name} (${waveLabel(d.wave)})`);
      const extra = binding.length > 2 ? ` +${binding.length - 2} more` : '';
      reasons.push(`Depends on ${shown.join(', ')}${extra}`);
    }

    // (d) engine cohesion
    (engineOfApp.get(i) || []).forEach((g) => {
      reasons.push(`Shares interface engine ${g.name} with ${g.count - 1} other app${g.count - 1 === 1 ? '' : 's'}`);
    });

    // (e) risk deferral
    if (deferHighRisk && isHighRisk(a)) {
      reasons.push('Mission Critical / DR Tier 1 — deferred one wave to earn confidence first');
    } else if (deferHighRisk && unit.highRisk) {
      reasons.push('Grouped with a Mission Critical / DR Tier 1 app — deferred with it');
    }

    // (f) capacity
    if (spilled.has(u) && wave > natural.wave[u]) {
      reasons.push(`Moved from ${waveLabel(natural.wave[u])} to respect the ${cap}-app cap per wave`);
    }

    // (g) nothing at all — say so loudly, this is an unknown not a certainty
    // "No dependency data at all" = this app recorded no integrations AND no
    // other app named it. Nobody has told us anything about how it connects.
    const noData = usableIntegrations(a).length === 0;
    const isolated = noData && inAdj[i].length === 0 && outAdj[i].length === 0;
    if (!reasons.length) {
      if (isolated) reasons.push('No integration data captured — earliest wave by default, NOT verified as dependency-free');
      else reasons.push('No unresolved dependencies — earliest wave');
    } else if (isolated) {
      reasons.push('No integration data captured on this app');
    }

    rows.push({
      id: a.id,
      app: a,
      name: a.name || '(unnamed)',
      unit: u,
      unitSize: unit.size,
      cycleSize: cycle.length,
      pinned: unit.pin !== null && respectPins && waveNumber(a.fields?.cmWave) !== null,
      highRisk: isHighRisk(a),
      noData,
      isolated,
      currentWave: current,
      currentNum: waveNumber(current),
      proposedWave: proposed,
      proposedNum: wave,
      changed: proposed !== current,
      reasons,
      why: reasons.join(' · '),
    });
  });

  rows.sort((a, b) => (a.proposedNum - b.proposedNum) || a.name.localeCompare(b.name));

  // --- Sanity numbers ------------------------------------------------------
  const engines = [...engineMembers.entries()]
    .map(([engineNode, memberSet]) => ({
      name: nodes[engineNode].name,
      inInventory: nodes[engineNode].kind === 'app',
      appCount: memberSet.size,
      waveNum: waveOfNode[engineNode],
      wave: waveLabel(waveOfNode[engineNode]),
      unified: acceptedGroups.some((g) => g.engineNode === engineNode),
      rejected: rejectedGroups.some((g) => g.engineNode === engineNode),
    }))
    .sort((a, b) => b.appCount - a.appCount || a.name.localeCompare(b.name));

  // A wave can legitimately hold no applications at all: an interface engine
  // with no fact sheet of its own still has to be stood up before the apps that
  // ride it, and that is a real wave of work. Name it rather than show a zero.
  const maxWave = Math.max(
    rows.reduce((m, r) => Math.max(m, r.proposedNum), 0),
    engines.reduce((m, e) => Math.max(m, e.waveNum), 0)
  );
  const perWave = [];
  for (let w = 1; w <= maxWave; w += 1) {
    const items = rows.filter((r) => r.proposedNum === w);
    perWave.push({
      wave: w,
      label: waveLabel(w),
      count: items.length,
      highRisk: items.filter((r) => r.highRisk).length,
      overCap: items.length > cap,
      infrastructure: engines.filter((e) => e.waveNum === w && !e.inInventory).map((e) => e.name),
    });
  }

  const cycles = comps
    .filter((c) => c.length > 1)
    .map((c) => ({
      size: c.length,
      wave: waveLabel(waveOfNode[c[0]]),
      members: c.map((n) => ({ name: nodes[n].name, id: nodes[n].app ? nodes[n].app.id : null, kind: nodes[n].kind })),
    }))
    .sort((a, b) => b.size - a.size);

  return {
    rows,
    perWave,
    cycles,
    engines,
    pinConflicts,
    rejectedGroups: rejectedGroups.map((g) => ({ name: g.name, appCount: g.appNodes.length })),
    oversized: overSizedUnits.map((u) => ({
      size: u.size,
      names: u.appNodes.map((n) => nodes[n].name),
    })),
    stats: {
      apps: rows.length,
      waves: maxWave,
      edges: edges.length,
      cycleCount: cycles.length,
      cycleApps: cycles.reduce((s, c) => s + c.members.filter((m) => m.kind === 'app').length, 0),
      noDataCount: rows.filter((r) => r.noData).length,
      isolatedCount: rows.filter((r) => r.isolated).length,
      externalEndpoints,
      engineCount: engines.length,
      beyondPicklist: Math.max(0, maxWave - STANDARD_WAVES.length),
      changes: rows.filter((r) => r.changed).length,
    },
  };
}

// ===========================================================================
// UI
// ===========================================================================
export default function WavePlanner({ tenantId, onOpen }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Every constraint is a visible control: the user must be able to see why an
  // application landed where it did, and change the rule rather than the answer.
  const [capChoice, setCapChoice] = useState(null); // null = no cap
  const [groupEngines, setGroupEngines] = useState(true);
  const [deferHighRisk, setDeferHighRisk] = useState(true);
  const [respectPins, setRespectPins] = useState(true);
  const [changesOnly, setChangesOnly] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedCount, setAppliedCount] = useState(null);

  const reload = useCallback(() => store.listFactSheets(tenantId, { type: 'Application' })
    .then((rows) => { setApps(rows || []); setLoading(false); }), [tenantId]);

  useEffect(() => { setLoading(true); reload(); }, [reload]);

  const capMax = Math.max(1, apps.length);
  const cap = capChoice === null ? capMax : Math.min(capChoice, capMax);
  const capped = cap < capMax;

  const hasIntegrations = useMemo(() => apps.some((a) => usableIntegrations(a).length > 0), [apps]);

  const plan = useMemo(
    () => buildPlan(apps, { maxPerWave: cap, groupEngines, deferHighRisk, respectPins }),
    [apps, cap, groupEngines, deferHighRisk, respectPins]
  );

  const visibleRows = useMemo(
    () => (changesOnly ? plan.rows.filter((r) => r.changed) : plan.rows),
    [plan, changesOnly]
  );

  const downloadCsv = () => {
    exportCsv('migration-wave-plan.csv', plan.rows, [
      { label: 'Application', value: (r) => r.name },
      { label: 'Current wave', value: (r) => r.currentWave },
      { label: 'Proposed wave', value: (r) => r.proposedWave },
      { label: 'Change', value: (r) => (r.changed ? 'Yes' : 'No') },
      { label: 'Pinned', value: (r) => (r.pinned ? 'Yes' : 'No') },
      { label: 'Mutual dependency group size', value: (r) => (r.cycleSize > 1 ? r.cycleSize : '') },
      { label: 'Mission Critical / DR Tier 1', value: (r) => (r.highRisk ? 'Yes' : 'No') },
      { label: 'Integration data captured', value: (r) => (r.noData ? 'No' : 'Yes') },
      { label: 'Why', value: (r) => r.why },
    ]);
  };

  const applyPlan = async () => {
    setApplying(true);
    const changes = plan.rows.filter((r) => r.changed);
    for (let i = 0; i < changes.length; i += 1) {
      const r = changes[i];
      // Sequential on purpose: the store write-through is per fact sheet and we
      // want a predictable, resumable order if anything fails mid-way.
      // eslint-disable-next-line no-await-in-loop
      await store.saveFactSheet({ ...r.app, fields: { ...(r.app.fields || {}), cmWave: r.proposedWave } });
    }
    await reload();
    setApplying(false);
    setConfirmOpen(false);
    setAppliedCount(changes.length);
  };

  const openApp = (id) => { if (onOpen && id) onOpen(id); };

  // --- Empty states --------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>Wave Planner</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!apps.length || !hasIntegrations) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>Wave Planner</Typography>
        <Alert severity="info" sx={{ maxWidth: 820 }}>
          <AlertTitle sx={{ fontWeight: 700, color: HEAD }}>
            {apps.length ? 'No integration data to plan against' : 'No applications in this workspace yet'}
          </AlertTitle>
          <Typography sx={{ fontSize: 13, color: GREY }}>
            Wave planning is a dependency calculation, not an opinion: it orders applications so that nothing
            cuts over before the systems it feeds. With no integrations recorded there is no graph to order,
            and any wave assignment would be arbitrary.
          </Typography>
          <Typography sx={{ fontSize: 13, color: GREY, mt: 1 }}>
            Integrations are captured per application on the fact sheet, under <b>Integrations &amp; Data Flow</b> —
            each row records the far end (<b>To</b>), the interface engine it routes through (<b>Via</b>),
            protocol, port and data type. The <b>Interface Hub</b> shows everything already captured across the
            estate; the <b>Survey</b> workflow is the fastest way to collect it from application owners.
            Once at least a handful of applications carry integrations, this screen will compute the plan.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const s = plan.stats;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Wave Planner</Typography>
        <HelpBubble topic="wavePlanner" />
        <Chip size="small" label={`${s.apps} applications · ${s.edges} dependency edges`}
          sx={{ height: 22, fontSize: 11, bgcolor: '#eef4fa', color: BLUE, fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={downloadCsv}>CSV</Button>
        <Button size="small" startIcon={<PlaylistAddCheckIcon />} variant="contained"
          disabled={s.changes === 0} onClick={() => setConfirmOpen(true)}>
          Apply plan{s.changes ? ` (${s.changes})` : ''}
        </Button>
      </Stack>

      <Typography sx={{ fontSize: 13, color: GREY, mb: 2, maxWidth: 900 }}>
        Computed, not generated. Applications are ordered by a topological sort of their integration graph, so a
        dependency is never scheduled after the application that needs it. Mutual dependencies cannot be ordered
        at all — those are reported below and scheduled together.
      </Typography>

      {appliedCount !== null && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAppliedCount(null)}>
          Wave assignment written to {appliedCount} application{appliedCount === 1 ? '' : 's'}.
          The Roadmap and Readiness views now reflect this plan.
        </Alert>
      )}

      {/* --- Constraints: the knobs that explain the answer ----------------- */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14, mb: 1 }}>Planning constraints</Typography>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1.5, alignItems: 'center' }}>
          <Box sx={{ minWidth: 260 }}>
            <Typography sx={{ fontSize: 12, color: GREY, fontWeight: 600 }}>
              Max applications per wave: <b style={{ color: NAVY }}>{capped ? cap : 'no limit'}</b>
            </Typography>
            <Slider
              size="small" min={1} max={capMax} value={cap}
              onChange={(e, v) => setCapChoice(v === capMax ? null : v)}
              valueLabelDisplay="auto"
              sx={{ color: BLUE, mt: 0.5 }}
            />
          </Box>

          <FormControlLabel
            control={<Switch size="small" checked={groupEngines} onChange={(e) => setGroupEngines(e.target.checked)} />}
            label={(
              <Typography sx={{ fontSize: 13, color: GREY }}>
                Keep apps on the same interface engine together
              </Typography>
            )}
          />
          <FormControlLabel
            control={<Switch size="small" checked={deferHighRisk} onChange={(e) => setDeferHighRisk(e.target.checked)} />}
            label={(
              <Typography sx={{ fontSize: 13, color: GREY }}>
                Schedule Mission Critical / DR Tier 1 later
              </Typography>
            )}
          />
          <FormControlLabel
            control={<Switch size="small" checked={respectPins} onChange={(e) => setRespectPins(e.target.checked)} />}
            label={(
              <Typography sx={{ fontSize: 13, color: GREY }}>
                Respect manually set waves as pins
              </Typography>
            )}
          />
        </Stack>
      </Paper>

      {/* --- Sanity panel --------------------------------------------------- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5, mb: 2 }}>
        <StatCard label="Waves in the plan" value={s.waves} accent={BLUE}
          note={s.beyondPicklist > 0
            ? `${s.beyondPicklist} beyond the ${STANDARD_WAVES.length} standard waves — extend the picklist in Lists Admin`
            : `${s.changes} application${s.changes === 1 ? '' : 's'} change wave`} />
        <StatCard label="Mutual dependency cycles" value={s.cycleCount} accent={s.cycleCount ? AMBER : GREEN}
          note={s.cycleCount
            ? `${s.cycleApps} applications cannot be ordered and must move together`
            : 'Every dependency can be strictly ordered'} />
        <StatCard label="No integration data at all" value={s.noDataCount} accent={s.noDataCount ? RED : GREEN}
          note={s.noDataCount
            ? 'These are unknowns, NOT confirmed-safe apps — an unrecorded interface is still an interface'
            : 'Every application carries at least one integration'} />
        <StatCard label="Shared interface engines" value={s.engineCount} accent={BLUE}
          note={`${s.externalEndpoints} integration endpoints point outside this inventory`} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5, mb: 2 }}>
        {plan.perWave.map((w) => (
          <Paper key={w.wave} sx={{ p: 1.25, borderTop: `3px solid ${w.overCap ? AMBER : BLUE}` }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{w.label}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>{w.count}</Typography>
            <Typography sx={{ fontSize: 11, color: GREY }}>
              applications{w.highRisk ? ` · ${w.highRisk} Mission Critical / DR Tier 1` : ''}
              {w.overCap ? ' · above the cap (indivisible group)' : ''}
            </Typography>
            {w.infrastructure.length > 0 && (
              <Typography sx={{ fontSize: 11, color: BLUE, mt: 0.25 }}>
                Stand up first: {w.infrastructure.join(', ')}
                {w.count === 0 ? ' — shared infrastructure only, no fact sheets to move' : ''}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      {/* --- Cycles: the honest bit ----------------------------------------- */}
      {plan.cycles.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <LoopIcon sx={{ fontSize: 18, color: AMBER }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>
              Mutual dependencies — cannot be ordered
            </Typography>
            <Chip size="small" label={plan.cycles.length} sx={{ height: 18 }} />
          </Stack>
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            These applications integrate with each other in both directions. There is no sequence that puts every
            dependency before its dependent, so the planner does not invent one: each group is scheduled as a
            single unit and must cut over together, in one change window.
          </Alert>
          <Stack divider={<Divider flexItem />} spacing={0}>
            {plan.cycles.map((c) => (
              <Stack key={c.members.map((m) => m.name).join('|')} direction="row" alignItems="center"
                spacing={1} sx={{ py: 0.6, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Chip size="small" label={`${c.size} apps together`}
                  sx={{ height: 18, fontSize: 10, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />
                <Chip size="small" label={c.wave} sx={{ height: 18, fontSize: 10 }} />
                {c.members.map((m) => (
                  <Box key={m.name} component="span" onClick={() => openApp(m.id)}
                    sx={{
                      fontSize: 12, fontWeight: 700, color: m.id ? BLUE : GREY,
                      cursor: m.id ? 'pointer' : 'default',
                      '&:hover': m.id ? { textDecoration: 'underline' } : undefined,
                    }}>
                    {m.name}
                  </Box>
                ))}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* --- Pin conflicts and cohesion rejections -------------------------- */}
      {(plan.pinConflicts.length > 0 || plan.rejectedGroups.length > 0 || plan.oversized.length > 0) && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 18, color: RED }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Constraints that could not all be met</Typography>
          </Stack>
          <Stack spacing={0.75}>
            {plan.pinConflicts.map((p) => (
              <Typography key={p.id} sx={{ fontSize: 12, color: GREY }}>
                <PushPinIcon sx={{ fontSize: 13, verticalAlign: 'middle', color: RED, mr: 0.5 }} />
                <b style={{ color: NAVY }}>{p.name}</b> — {p.detail}. The pin was honoured; the dependency will be
                live in a different environment at cutover.
              </Typography>
            ))}
            {plan.rejectedGroups.map((g) => (
              <Typography key={g.name} sx={{ fontSize: 12, color: GREY }}>
                <HubIcon sx={{ fontSize: 13, verticalAlign: 'middle', color: AMBER, mr: 0.5 }} />
                Interface engine <b style={{ color: NAVY }}>{g.name}</b> ({g.appCount} apps) could not be kept
                together — one of its applications depends on another through a different path, so grouping them
                would create an impossible order.
              </Typography>
            ))}
            {plan.oversized.map((o) => (
              <Typography key={o.names.join('|')} sx={{ fontSize: 12, color: GREY }}>
                <LoopIcon sx={{ fontSize: 13, verticalAlign: 'middle', color: AMBER, mr: 0.5 }} />
                A group of {o.size} applications ({o.names.slice(0, 4).join(', ')}
                {o.names.length > 4 ? `, +${o.names.length - 4} more` : ''}) exceeds the {cap}-app cap but cannot be
                split — they are mutually dependent or share an engine.
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}

      {/* --- Interface engines --------------------------------------------- */}
      {plan.engines.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <HubIcon sx={{ fontSize: 18, color: BLUE }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Shared infrastructure</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {plan.engines.map((e) => (
              <Tooltip key={e.name}
                title={`${e.appCount} applications route through ${e.name}${e.inInventory ? '' : ' (no fact sheet in this inventory)'}`}>
                <Chip size="small"
                  label={`${e.name} · ${e.appCount} apps · ${e.wave}`}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 600,
                    bgcolor: e.unified ? '#eef4fa' : '#f5f5f5',
                    color: e.unified ? BLUE : GREY,
                    border: e.rejected ? `1px solid ${AMBER}` : '1px solid #e0e0e0',
                  }} />
              </Tooltip>
            ))}
          </Stack>
        </Paper>
      )}

      {/* --- The plan ------------------------------------------------------- */}
      <Paper sx={{ p: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Proposed plan</Typography>
          <Chip size="small" label={`${s.changes} of ${s.apps} change wave`}
            sx={{ height: 18, fontSize: 11, bgcolor: s.changes ? '#FFF3E0' : '#E8F5E9', color: s.changes ? '#E65100' : GREEN, fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Switch size="small" checked={changesOnly} onChange={(e) => setChangesOnly(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: GREY }}>Changes only</Typography>}
          />
        </Stack>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f8fc' }}>
              <TableCell sx={{ fontWeight: 700, color: HEAD, fontSize: 12 }}>Application</TableCell>
              <TableCell sx={{ fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' }}>Current wave</TableCell>
              <TableCell sx={{ fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' }}>Proposed wave</TableCell>
              <TableCell sx={{ fontWeight: 700, color: HEAD, fontSize: 12 }}>Why</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontSize: 12 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box component="span" onClick={() => openApp(r.id)}
                      sx={{ fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                      {r.name}
                    </Box>
                    {r.pinned && <Tooltip title="Wave set manually — honoured as a pin"><PushPinIcon sx={{ fontSize: 13, color: GREY }} /></Tooltip>}
                    {r.cycleSize > 1 && <Tooltip title={`Mutual dependency group of ${r.cycleSize}`}><LoopIcon sx={{ fontSize: 14, color: AMBER }} /></Tooltip>}
                    {r.isolated && <Tooltip title="No integration data captured — unknown, not safe"><HelpOutlineIcon sx={{ fontSize: 14, color: RED }} /></Tooltip>}
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: GREY, whiteSpace: 'nowrap' }}>{r.currentWave}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Chip size="small" label={r.proposedWave}
                    sx={{
                      height: 19, fontSize: 11, fontWeight: 700,
                      bgcolor: r.changed ? '#FFF3E0' : '#eef4fa',
                      color: r.changed ? '#E65100' : BLUE,
                    }} />
                </TableCell>
                <TableCell sx={{ fontSize: 11, color: GREY }}>{r.why}</TableCell>
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ fontSize: 12, color: GREY, py: 2 }}>
                  Every application is already in the wave this plan computes — nothing to change.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* --- Apply confirmation --------------------------------------------- */}
      <Dialog open={confirmOpen} onClose={applying ? undefined : () => setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Apply the computed wave plan?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: GREY, mb: 1.5 }}>
            This writes <b>CM: Selected for Wave</b> on <b style={{ color: NAVY }}>{s.changes}</b> of {s.apps} applications.
            Applications already in their computed wave are left untouched.
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {plan.perWave.map((w) => {
              const moving = plan.rows.filter((r) => r.changed && r.proposedNum === w.wave).length;
              if (!moving) return null;
              return (
                <Typography key={w.wave} sx={{ fontSize: 12, color: GREY }}>
                  <b style={{ color: NAVY }}>{w.label}</b> — {moving} application{moving === 1 ? '' : 's'} moving in
                </Typography>
              );
            })}
          </Stack>
          {plan.pinConflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
              {plan.pinConflicts.length} manual pin{plan.pinConflicts.length === 1 ? '' : 's'} contradict the dependency
              graph. The pins are kept as set — review them before the cutover plan is signed off.
            </Alert>
          )}
          {plan.cycles.length > 0 && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              {plan.cycles.length} mutual dependency group{plan.cycles.length === 1 ? '' : 's'} will be written to a
              single shared wave — those applications have to cut over together.
            </Alert>
          )}
          {applying && <LinearProgress sx={{ mt: 1.5 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>Cancel</Button>
          <Button variant="contained" onClick={applyPlan} disabled={applying}>
            {applying ? 'Writing…' : `Apply to ${s.changes} applications`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function StatCard({ label, value, note, accent }) {
  return (
    <Paper sx={{ p: 1.25, borderTop: `3px solid ${accent}` }}>
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: GREY }}>{note}</Typography>
    </Paper>
  );
}
